import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { ScanSearch, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Recon Auto-Watch — surfaces the reconciliation flag count for Admin/Accounts
 * without opening the full report. Reuses the existing reconciliation scan
 * (GET /api/invoices/reconciliation-count). Renders nothing when there are no
 * flags or for roles that can't view the report.
 */
export default function ReconWatchChip() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const canView = role === 'admin' || role === 'accounts';
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!canView) return;
    let alive = true;
    axios.get(`${BACKEND_URL}/api/invoices/reconciliation-count`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canView]);

  if (!canView || !data || !data.flaggedCount) return null;

  const c = data.counts || {};
  const parts = [];
  if (c.dedupeSuspected) parts.push(`${c.dedupeSuspected} possible double-entry`);
  if (c.overpaid) parts.push(`${c.overpaid} overpaid`);
  if (c.statusMismatch) parts.push(`${c.statusMismatch} status mismatch`);

  return (
    <Link
      to="/reports/reconciliation"
      data-testid="recon-watch-chip"
      className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 hover:bg-amber-100 transition-colors"
    >
      <ScanSearch className="h-4 w-4 shrink-0 text-amber-600" />
      <span className="text-sm font-semibold" data-testid="recon-watch-count">
        {data.flaggedCount} reconciliation flag{data.flaggedCount === 1 ? '' : 's'}
      </span>
      {parts.length > 0 && (
        <span className="text-xs text-amber-700 truncate hidden sm:inline">· {parts.join(' · ')}</span>
      )}
      <ChevronRight className="h-4 w-4 ml-auto shrink-0 text-amber-500" />
    </Link>
  );
}
